import {
    Featured,
    ForYou,
    FeaturedBpmOriginal,
    PromotionsMessagingModal,
    useLibraryTabs,
    TrendingBrowseSupreme,
    ExclusivePlaylistsBrowse,
    FeaturedArtists,
    DownloadPageLayout,
    FeaturedEditors,
    PlaylistsBrowse,
    CuratedSetBrowse
} from '@bpm-web-app/components';
import { Banner, MessageBanner, AnonymousUserBanner } from '@bpm-web-app/components/shared';
import { useHubSwitch, useUserSettings } from '@bpm-web-app/utils';
import { useEffect, useMemo, useState } from 'react';
import { useGetSectionsDownload } from '@bpm-web-app/swr-hooks';
import { Section } from '@bpm-web-app/download-api-sdk';

export default function Index({ title }: { title: string }) {
    const { isDownload } = useHubSwitch();
    const { isAnonymous } = useUserSettings();
    const { libraryProperty } = useLibraryTabs();

    const { data: sectionsData, isLoading: isLoadingSectionData } = useGetSectionsDownload('browse', libraryProperty);

    const sectionsToLoad = useMemo(() => {
        return sectionsData?.data?.filter((section) => {
            if (isAnonymous) {
                if (section.name === 'For You') {
                    return false;
                }
            }
            if (section.name === 'AnonymousUserBanner') {
                return false;
            }
            return true;
        }).map((section) => section.name) || [];
    }, [isAnonymous, sectionsData?.data]);

    const [loadedSection, setSectionsLoaded] = useState<string[]>([]);

    useEffect(() => {
        setSectionsLoaded([]);
    }, [libraryProperty, isAnonymous]);

    const [isBannersLoaded, setIsBannersLoaded] = useState(false);

    const isEverythingLoaded = useMemo(() => {
        if (!isBannersLoaded) return false;

        return sectionsToLoad.every((section) => loadedSection.includes(section));
    }, [isBannersLoaded, loadedSection, sectionsToLoad]);

    return (
        <DownloadPageLayout title={title}>
            {isAnonymous ? <MessageBanner type="info" title="You’re currently previewing BPM Supreme." viewPlanRoute /> : null}
            <Banner platform={isDownload ? 'download' : 'stream'} library={libraryProperty} isDataLoaded={isEverythingLoaded} setIsDataLoaded={setIsBannersLoaded} />
            {!isLoadingSectionData && sectionsData?.data?.map((section) => {
                if (section.type === Section.TypeEnum.Virtual) {
                    switch (section.name) {
                        case 'Exclusives':
                            return (
                                <FeaturedBpmOriginal
                                    isDataLoaded={isEverythingLoaded}
                                    setIsDataLoaded={() => {
                                        setSectionsLoaded((loaded) => {
                                            if (!loadedSection.includes(section.name)) {
                                                return [...loaded, section.name];
                                            }
                                            return loaded;
                                        });
                                    }} />
                            );
                        case 'Remixers & Editors': {
                            return (
                                <FeaturedEditors
                                    isDataLoaded={isEverythingLoaded}
                                    setIsDataLoaded={() => {
                                        setSectionsLoaded((loaded) => {
                                            if (!loadedSection.includes(section.name)) {
                                                return [...loaded, section.name];
                                            }
                                            return loaded;
                                        });
                                    }} />
                            );
                        }
                        case 'New Music':
                            return <Featured
                                seeMorePath="/releases"
                                isDataLoaded={isEverythingLoaded}
                                setIsDataLoaded={() => {
                                    setSectionsLoaded((loaded) => {
                                        if (!loadedSection.includes(section.name)) {
                                            return [...loaded, section.name];
                                        }
                                        return loaded;
                                    });
                                }} />;
                        case 'Featured Artists':
                            return <FeaturedArtists
                                isDataLoaded={isEverythingLoaded}
                                setIsDataLoaded={() => {
                                    setSectionsLoaded((loaded) => {
                                        if (!loadedSection.includes(section.name)) {
                                            return [...loaded, section.name];
                                        }
                                        return loaded;
                                    });
                                }} />;
                        case 'AnonymousUserBanner':
                            return <AnonymousUserBanner />;
                        case 'Exclusive Playlists':
                            return <ExclusivePlaylistsBrowse
                                isDataLoaded={isEverythingLoaded}
                                setIsDataLoaded={() => {
                                    setSectionsLoaded((loaded) => {
                                        if (!loadedSection.includes(section.name)) {
                                            return [...loaded, section.name];
                                        }
                                        return loaded;
                                    });
                                }} />;
                        case 'Top 10':
                            return <TrendingBrowseSupreme
                                isDataLoaded={isEverythingLoaded}
                                setIsDataLoaded={() => {
                                    setSectionsLoaded((loaded) => {
                                        if (!loadedSection.includes(section.name)) {
                                            return [...loaded, section.name];
                                        }
                                        return loaded;
                                    });
                                }} />;

                        case 'For You': {
                            if (isAnonymous) {
                                return null;
                            }
                            return (
                                <ForYou
                                    isDataLoaded={isEverythingLoaded}
                                    setIsDataLoaded={() => {
                                        setSectionsLoaded((loaded) => {
                                            if (!loadedSection.includes(section.name)) {
                                                return [...loaded, section.name];
                                            }
                                            return loaded;
                                        });
                                    }} />
                            );
                        }
                        default:
                            return null;
                    }
                } else {
                    if (section.PlaylistCategory?.id) {
                        return <PlaylistsBrowse
                            isDataLoaded={isEverythingLoaded}
                            categoryId={section.PlaylistCategory.id}
                            title={section.name}
                            setIsDataLoaded={() => {
                                setSectionsLoaded((loaded) => {
                                    if (!loadedSection.includes(section.name)) {
                                        return [...loaded, section.name];
                                    }
                                    return loaded;
                                });
                            }} />;
                    } if (section.CuratedSetCategory?.id) {
                        return <CuratedSetBrowse
                            isDataLoaded={isEverythingLoaded}
                            categoryId={section.CuratedSetCategory.id}
                            title={section.name}
                            setIsDataLoaded={() => {
                                setSectionsLoaded((loaded) => {
                                    if (!loadedSection.includes(section.name)) {
                                        return [...loaded, section.name];
                                    }
                                    return loaded;
                                });
                            }} />;
                    }
                    // eslint-disable-next-line no-underscore-dangle
                    return null;
                }
            })}
            <PromotionsMessagingModal />
        </DownloadPageLayout>
    );
}

export async function getStaticProps() {
    return {
        props: {
            title: 'Home',
        },
    };
}
